import { useEffect, useState } from "react";

/**
 * Retrieves a value from localStorage, parsing it as JSON.
 * @param {string} key - The key to look up.
 * @param {*} defaultValue - The value to return if the key doesn't exist.
 * @returns {*} The parsed value or defaultValue if not found/invalid.
 */
export function getStorageValue(key, defaultValue) {
  const saved = localStorage.getItem(key);
  if (saved === null) return defaultValue;
  try {
    return JSON.parse(saved);
  } catch (error) {
    console.error("Error parsing localStorage value:", error);
    return defaultValue; // Fallback to default if JSON is invalid
  }
}

/**
 * Sets or removes a value in localStorage, serializing it as JSON.
 * @param {string} key - The key to set.
 * @param {*} value - The value to store (or null/undefined to remove).
 */
export function setStorageValue(key, value) {
  if (value === null || value === undefined) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, JSON.stringify(value));
  }
}

/**
 * A React hook to manage a value in localStorage with state synchronization.
 * @param {string} key - The localStorage key.
 * @param {*} defaultValue - The default value if the key doesn't exist.
 * @returns {[*, Function]} An array with the current value and a setter function.
 */
export const useLocalStorage = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    return getStorageValue(key, defaultValue);
  });

  // Update localStorage when key or value changes
  useEffect(() => {
    setStorageValue(key, value);
  }, [key, value]);

  // Optional: Sync state with changes from other tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === key) {
        setValue(event.newValue ? JSON.parse(event.newValue) : defaultValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [key, defaultValue]);

  return [value, setValue];
};